import { useCallback, useEffect, useState } from "react";
import { ApiError, getApi, ZOD_PARSE_ERROR } from "../utils/api.ts";
import { accessToken } from "../utils/auth.ts";
import { GET_EMPLOYEE_PROFILE_BY_EMPID } from "../../config/Endpoints.js";
import {
    EmployeePersonalDetail,
    EmployeeProfileResponseSchema,
} from "../schemas/employee-profile.schema.ts";

export function useEmployeeProfile(employeeId: number | null) {
    const [personalDetail, setPersonalDetail] = useState<EmployeePersonalDetail | null>(
        null,
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProfile = useCallback(
        async (signal?: AbortSignal) => {
            if (employeeId == null) {
                setPersonalDetail(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const url = `${GET_EMPLOYEE_PROFILE_BY_EMPID}/${employeeId}`;
                const { data } = await getApi<unknown>(
                    url,
                    accessToken(),
                    "json",
                    signal,
                );

                const parse = EmployeeProfileResponseSchema.safeParse(data);

                if (!parse.success) {
                    setPersonalDetail(null);
                    setError(ZOD_PARSE_ERROR);
                    return;
                }

                setPersonalDetail(parse.data.data.personalDetail);
            } catch (e) {
                const msg =
                    e instanceof ApiError
                        ? e.message
                        : "Failed to load employee profile.";
                setError(msg);
                setPersonalDetail(null);
            } finally {
                setLoading(false);
            }
        },
        [employeeId],
    );

    useEffect(() => {
        const ac = new AbortController();
        void fetchProfile(ac.signal);
        return () => ac.abort();
    }, [fetchProfile]);

    return { personalDetail, loading, error, refetch: fetchProfile };
}
